import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from './supabaseClient';

/**
 * Default grid layouts for single and dual foot modes.
 * Grid uses 12 columns; each unit row = 80px.
 * Widget IDs match keys in widgetRegistry.js.
 */
const DEFAULT_LAYOUTS = {
  single: [
    { i: 'pressure_heatmap', x: 0, y: 0, w: 5, h: 6 },
    { i: 'pressure_chart',   x: 5, y: 0, w: 7, h: 3 },
    { i: 'histogram',        x: 5, y: 3, w: 4, h: 3 },
    { i: 'statistics',       x: 9, y: 3, w: 3, h: 3 },
    { i: 'video_feed',       x: 0, y: 6, w: 5, h: 5 },
  ],
  dual: [
    { i: 'pressure_heatmap_left',  x: 0, y: 0, w: 4, h: 6 },
    { i: 'statistics',             x: 4, y: 0, w: 4, h: 4 },
    { i: 'pressure_heatmap_right', x: 8, y: 0, w: 4, h: 6 },
    { i: 'pressure_chart_left',    x: 0, y: 6, w: 6, h: 3 },
    { i: 'pressure_chart_right',   x: 6, y: 6, w: 6, h: 3 },
    { i: 'donut_left',             x: 4, y: 4, w: 2, h: 2 },
    { i: 'donut_right',            x: 6, y: 4, w: 2, h: 2 },
    { i: 'video_feed',             x: 8, y: 4, w: 4, h: 4 },
  ],
};

const DEFAULT_VISIBLE = {
  single: ['pressure_heatmap', 'pressure_chart', 'histogram', 'statistics', 'video_feed'],
  dual: ['pressure_heatmap_left', 'pressure_heatmap_right', 'statistics',
         'pressure_chart_left', 'pressure_chart_right', 'donut_left', 'donut_right', 'video_feed'],
};

const SAVE_DEBOUNCE_MS = 1000;

/**
 * Loads and persists the dashboard layout for one mode per user.
 * Falls back to DEFAULT_LAYOUTS if no saved layout exists.
 */
export function useDashboardLayout(userId, mode) {
  const [layout, setLayout] = useState(DEFAULT_LAYOUTS[mode] || []);
  const [visibleWidgets, setVisibleWidgets] = useState(new Set(DEFAULT_VISIBLE[mode] || []));
  const [isLoading, setIsLoading] = useState(true);

  const saveTimerRef = useRef(null);

  // Load from Supabase on mount / mode change
  useEffect(() => {
    if (!userId) {
      setLayout(DEFAULT_LAYOUTS[mode] || []);
      setVisibleWidgets(new Set(DEFAULT_VISIBLE[mode] || []));
      setIsLoading(false);
      return;
    }

    const load = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('dashboard_layouts')
          .select('layout, visible_widgets')
          .eq('user_id', userId)
          .eq('mode', mode)
          .single();

        if (error || !data) {
          setLayout(DEFAULT_LAYOUTS[mode] || []);
          setVisibleWidgets(new Set(DEFAULT_VISIBLE[mode] || []));
        } else {
          setLayout(data.layout || DEFAULT_LAYOUTS[mode] || []);
          setVisibleWidgets(new Set(data.visible_widgets || DEFAULT_VISIBLE[mode] || []));
        }
      } catch {
        setLayout(DEFAULT_LAYOUTS[mode] || []);
        setVisibleWidgets(new Set(DEFAULT_VISIBLE[mode] || []));
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [userId, mode]);

  const persistLayout = useCallback(async (newLayout, newVisible) => {
    if (!userId) return;

    try {
      await supabase
        .from('dashboard_layouts')
        .upsert(
          {
            user_id: userId,
            mode,
            layout: newLayout,
            visible_widgets: Array.from(newVisible),
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,mode' }
        );
    } catch (err) {
      console.error('Failed to save layout:', err);
    }
  }, [userId, mode]);

  const saveLayout = useCallback((newLayout, newVisible) => {
    setLayout(newLayout);
    if (newVisible !== undefined) setVisibleWidgets(newVisible);

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      persistLayout(newLayout, newVisible ?? visibleWidgets);
    }, SAVE_DEBOUNCE_MS);
  }, [persistLayout, visibleWidgets]);

  const toggleWidget = useCallback((widgetId) => {
    setVisibleWidgets(prev => {
      const next = new Set(prev);
      if (next.has(widgetId)) {
        next.delete(widgetId);
      } else {
        next.add(widgetId);
        // Add to end of layout if not already there
        setLayout(prevLayout => {
          if (prevLayout.some(item => item.i === widgetId)) return prevLayout;
          const defaults = DEFAULT_LAYOUTS[mode] || [];
          const defaultItem = defaults.find(d => d.i === widgetId);
          const newItem = defaultItem
            ? { ...defaultItem }
            : { i: widgetId, x: 0, y: Infinity, w: 3, h: 4 };
          return [...prevLayout, newItem];
        });
      }

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        persistLayout(layout, next);
      }, SAVE_DEBOUNCE_MS);

      return next;
    });
  }, [persistLayout, layout, mode]);

  return { layout, visibleWidgets, isLoading, saveLayout, toggleWidget };
}
